import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Card, Button, PageTitle } from "../../../../v2/design-system";
import { theme } from "../../../../v2/design-system/theme";
import OrgWalletLedgerTable from "./OrgWalletLedgerTable";
import { getBalancePrefunded, getOrgWalletLedger } from "../../../../utils/api";
import { CsvDownApi } from "../../../../v2/integrations/export";
import config from "../../../../utils/config";
import { HubFlexRow, HubPageShell } from "../societyHubShell";
import { v2PaginationOneBased } from "../tableShared";

const BalanceCard = styled(Card)`
  width: 20%;
  max-width: 320px;
  min-width: 180px;
  padding: ${theme.spacing.md};
  box-sizing: border-box;
`;

const BalanceLabel = styled.p`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin: 0 0 ${theme.spacing.xs};
`;

const BalanceValue = styled.p`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.purple};
  margin: 0;
`;

const MiniSpinner = styled.span`
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 2px solid ${theme.colors.border};
  border-top-color: ${theme.colors.purple};
  border-radius: 50%;
  animation: org-hub-spin 0.65s linear infinite;
  @keyframes org-hub-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const OrgWalletLedger = ({ userId }) => {
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const [showLedger, setShowLedger] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);

  const [txnPage, setTxnPage] = useState(1);
  const [itemsPerPageTxn, setItemsPerPageTxn] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchBalance = async () => {
    try {
      setLoadingBalance(true);
      const response = await getBalancePrefunded({ orgUserId: userId });
      const data = response?.data?.data;
      setBalance(data?.balanceInBaseCurrency ?? 0);
    } catch {
      setBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  useEffect(() => {
    if (userId) fetchBalance();
  }, [userId]);

  const fetchLedger = async (page = txnPage, size = itemsPerPageTxn) => {
    try {
      setLoadingLedger(true);
      const payload = {
        orgUserId: userId,
        pageNo: page - 1,
        pageSize: size,
      };
      const response = await getOrgWalletLedger(payload);
      const data = response?.data?.data;
      setLedgerData(data?.content || []);
      setTotalRecords(data?.totalElements || 0);
    } catch {
      setLedgerData([]);
      setTotalRecords(0);
    } finally {
      setLoadingLedger(false);
    }
  };

  useEffect(() => {
    if (showLedger && userId) {
      fetchLedger();
    }
  }, [showLedger, txnPage, itemsPerPageTxn, userId]);

  const handleViewLedger = () => {
    setShowLedger(true);
  };

  const csvHeaders = [
    { key: "voucherId", label: "Transaction ID" },
    { key: "txnAmount", label: "Transaction Amount" },
    { key: "openingBalance", label: "Opening Balance" },
    { key: "closingBalance", label: "Closing Balance" },
    { key: "txnType", label: "Transaction Type" },
    { key: "narration", label: "Narration" },
    { key: "txnMode", label: "Transaction Mode" },
    { key: "finalAmount", label: "Final Amount" },
    { key: "gstCharges", label: "GST Charges" },
    { key: "totalServiceFee", label: "Total Service Fee" },
    { key: "txnStatus", label: "Status" },
  ];

  const csvParams = {
    orgUserId: userId,
    pageNo: txnPage - 1,
    pageSize: itemsPerPageTxn,
  };

  return (
    <HubPageShell>
      <HubFlexRow $mb={theme.spacing.md}>
        <PageTitle style={{ margin: 0 }}>Wallet Details</PageTitle>
      </HubFlexRow>

      <HubFlexRow $mb={theme.spacing.lg}>
        <BalanceCard>
          <BalanceLabel>Account Balance</BalanceLabel>
          {loadingBalance ? (
            <MiniSpinner aria-hidden />
          ) : (
            <BalanceValue>₹{balance !== null ? balance.toLocaleString() : "N/A"}</BalanceValue>
          )}
        </BalanceCard>
      </HubFlexRow>

      {!showLedger ? (
        <Button type="button" variant="primary" size="md" onClick={handleViewLedger}>
          View Ledger
        </Button>
      ) : (
        <>
          <HubFlexRow $justify="flex-end" $mb={theme.spacing.md} $mt={theme.spacing.sm}>
            {ledgerData.length > 0 && (
              <CsvDownApi
                headers={csvHeaders}
                url={`${config.apis.societyPartnerHub.LEDGER_LIST}`}
                params={csvParams}
                method="post"
                reportName="OrgWalletLedger.csv"
                title="Download CSV"
              />
            )}
          </HubFlexRow>
          <OrgWalletLedgerTable
            userId={userId}
            data={ledgerData}
            loading={loadingLedger}
            pagination={v2PaginationOneBased({
              pageOneBased: txnPage,
              pageSize: itemsPerPageTxn,
              total: totalRecords,
              setPageOneBased: setTxnPage,
              setPageSize: setItemsPerPageTxn,
            })}
          />
        </>
      )}
    </HubPageShell>
  );
};

export default OrgWalletLedger;
